/**
 * What each mutating control in the Secrets area requires — read off the
 * `requirePermission` middleware in `backend/src/rest/rest-team-secrets.ts`,
 * not off the label.
 *
 * That file declares exactly two checks and uses one of them for every write:
 *
 *   GET    /api/teams/:teamId/secrets                       TEAM_SECRET: view
 *   GET    /api/teams/:teamId/secrets/activity              TEAM_SECRET: view
 *   GET    /api/teams/:teamId/secrets/:id                   TEAM_SECRET: view
 *   POST   /api/teams/:teamId/secrets                       TEAM_SECRET: manage
 *   PATCH  /api/teams/:teamId/secrets/:id                   TEAM_SECRET: manage
 *   POST   .../secrets/:id/rotate                           TEAM_SECRET: manage
 *   DELETE .../secrets/:id                                  TEAM_SECRET: manage
 *   PUT    .../secrets/:id/grants/:agentId                  TEAM_SECRET: manage
 *   DELETE .../secrets/:id/grants/:agentId                  TEAM_SECRET: manage
 *   POST   .../secrets/:id/versions/:versionId/restore      TEAM_SECRET: manage
 *   DELETE .../secrets/:id/versions/:versionId              TEAM_SECRET: manage
 *
 * So nothing here is guarded at `edit`, and that is deliberate rather than an
 * oversight to be tidied up: TEAM_SECRET's ladder is unusually meaningful —
 * `view` is secret NAMES, metadata, and protected agent requests (no serializer
 * in that file ever emits a value), `edit` lets humans answer those requests
 * through the protected approval flow, and `manage` is the direct human
 * create/rotate/delete/grant these screens perform. Guarding a create at
 * `edit` would promise the vault to somebody the route refuses.
 *
 * TEAM_SECRET is `team_only` in `RESOURCE_SCOPE`, so no check below passes a
 * `projectId`: a project-scoped grant of it cannot exist, and passing an id
 * would invite the reader to think one could.
 *
 * None of this is the boundary — `requirePermission` on the route is. These
 * entries only decide whether a click performs the action or explains which
 * grant is missing, before a form is filled in.
 */

import {
  PermissionLevel,
  PermissionResource,
} from '../permissions/permission-catalog';

/** A (resource, level) pair plus phrase overrides for one action, ready to
 *  spread into `GuardedActionButton` / `useGuardedCallback`. */
export interface SecretAction {
  resource: PermissionResource;
  level: PermissionLevel;
  verb?: string;
  subject?: string;
}

const SECRET = PermissionResource.TEAM_SECRET;
const { MANAGE } = PermissionLevel;

export const SECRET_ACTION: Record<string, SecretAction> = {
  CREATE: { resource: SECRET, level: MANAGE, verb: 'create' },
  // PATCH carries the name, description, destination policy and retention.
  EDIT: { resource: SECRET, level: MANAGE, verb: 'change' },
  ROTATE: { resource: SECRET, level: MANAGE, verb: 'rotate' },
  DELETE: { resource: SECRET, level: MANAGE, verb: 'delete' },
  VERSION_RESTORE: {
    resource: SECRET,
    level: MANAGE,
    verb: 'restore',
    subject: 'secret versions',
  },
  VERSION_DELETE: {
    resource: SECRET,
    level: MANAGE,
    verb: 'delete',
    subject: 'secret versions',
  },
  GRANT: {
    resource: SECRET,
    level: MANAGE,
    verb: 'assign',
    subject: 'team secrets to agents',
  },
  UNGRANT: {
    resource: SECRET,
    level: MANAGE,
    verb: 'unassign',
    subject: 'team secrets from agents',
  },
};

export default SECRET_ACTION;
